import { useQuery } from '@tanstack/react-query'
import { t } from '@stokr/components-library'
import type { CopyKey } from 'locales/copy-keys.gen'
import { formatUtcDate } from 'utils/formatUtcDate'
import { useStoProject } from './useStoProject'
import { stoQueries } from './queries/stoQueries'
import {
  pickContent,
  StoContentTypes,
  type DividendsContent,
  type HistoryColumn,
  type HistoryDividendContent,
} from 'models/projectContent'
import { resolveSectionState, type SectionState } from 'models/stoSection'
import { isMetricLinkValue } from 'models/metric'

export interface DividendColumn {
  key: string
  label: string
}

/** The dividend distributions, one expandable row each, with the columns back-office sends. */
export interface DividendHistory {
  /** The author's heading over the rows, e.g. "Declaration". */
  title?: string
  /** Rich text under the title. */
  subtitle?: string
  /** Info tooltip next to the title. */
  tooltip?: string
  /** Back-office columns, in back-office order: the first few show on a closed row. */
  columns: DividendColumn[]
  /** A column holding a link (the declaration): a button, not a figure. */
  linkColumn?: DividendColumn
  rows: Record<string, string>[]
}

const clean = (text: string | undefined): string => text?.trim() ?? ''

/** A row with nothing in any column is left out. */
const hasAny = (row: Record<string, string> | undefined, keys: readonly string[]): boolean =>
  keys.some((key) => clean(row?.[key]))

/** Labels for the legacy `isFixed` history columns, whose stored label was never the shown one. */
const FIXED_HISTORY_LABELS: Record<string, CopyKey> = {
  date: 'stoPage.dividends.history.recordDate',
  type: 'stoPage.dividends.history.eventType',
}

const historyColumnLabel = ({ key, label, isFixed }: HistoryColumn): string =>
  (isFixed && FIXED_HISTORY_LABELS[key] && t(FIXED_HISTORY_LABELS[key])) || label

/** Null when there is nothing to show, which hides the section. */
export const toDividendHistory = (history: HistoryDividendContent | undefined): DividendHistory | null => {
  if (!history) return null
  const all = (history.columns ?? [])
    .map((column) => ({ ...column, key: clean(column.key), label: clean(column.label) }))
    .filter((column) => column.key)
    .map((column) => ({ key: column.key, label: historyColumnLabel(column) }))
  const keys = all.map((column) => column.key)
  const rows = (history.entries ?? []).filter((row) => hasAny(row, keys))
  if (all.length === 0 || rows.length === 0) return null

  // The first column with a link in any row; its "-" placeholders are no link and show nothing.
  const linkColumn = all.find((column) => rows.some((row) => isMetricLinkValue(clean(row[column.key]))))
  return {
    title: clean(history.title) || undefined,
    subtitle: clean(history.subtitle) || undefined,
    tooltip: clean(history.tooltip) || undefined,
    columns: all.filter((column) => column !== linkColumn),
    linkColumn,
    rows,
  }
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']

/** `YYYY-MM-DD` of a calendar day. */
const dayKey = (year: number, month: number, day: number): string =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

/**
 * Back-office dates are free text: "15 Oct 2026", "2 Sept 2026", "15 October 2026", "31-Oct-26"
 * (spreadsheet imports) or "2026-10-15". Anything else is not a date (undefined).
 */
export const toDividendDay = (text: string | undefined): string | undefined => {
  const value = clean(text)
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (iso) return dayKey(Number(iso[1]), Number(iso[2]), Number(iso[3]))
  const named = /^(\d{1,2})[\s-]+([A-Za-z]+)\.?[\s-]+(\d{4}|\d{2})$/.exec(value)
  const month = named ? MONTHS.indexOf(named[2].slice(0, 3).toLowerCase()) + 1 : 0
  if (!named || month === 0) return undefined
  const year = named[3].length === 2 ? 2000 + Number(named[3]) : Number(named[3])
  return dayKey(year, month, Number(named[1]))
}

/** `31 Oct 2026` whatever way back-office wrote the date (`31-Oct-26`); text that is no date stays as typed. */
export const formatDividendDate = (text: string): string => {
  const day = toDividendDay(text)
  return (day && formatUtcDate(`${day}T00:00:00Z`)) || text
}

const useDividendHistory = () => {
  const { projectId } = useStoProject()
  const query = useQuery(stoQueries.content(projectId))
  const history = toDividendHistory(
    pickContent<Partial<DividendsContent>>(query.data, StoContentTypes.DIVIDENDS)?.history,
  )
  return { query, history }
}

/** The `dividends` record's history, from the page's single project-content request. */
export function useDividends(): SectionState<DividendHistory> {
  const { query, history } = useDividendHistory()
  return resolveSectionState({
    isPending: query.isPending,
    isError: query.isError,
    data: history ?? undefined,
    isEmpty: () => false,
  })
}

/** Whether the product pays dividends: the small box in the hero links to the section. */
export function useHasDividends(): boolean {
  return Boolean(useDividendHistory().history)
}
